import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { BarChart3, FileDown, FileSpreadsheet, FileText, Loader2, ClipboardCheck, AlarmClock, Archive, AlertTriangle, FileArchive } from "lucide-react";

function BarList({ title, data, color = "#1e3a5f" }) {
  const max = Math.max(1, ...(data || []).map((d) => d.count));
  return (
    <Card className="p-4" data-testid={`report-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h4 className="font-head font-bold text-slate-900 text-sm mb-3">{title}</h4>
      {(!data || data.length === 0) ? <p className="text-sm text-slate-400">Keine Daten.</p>
        : <div className="space-y-2">
            {data.slice(0, 8).map((d) => (
              <div key={d.label} className="flex items-center gap-2">
                <span className="text-xs text-slate-600 w-32 truncate shrink-0">{d.label}</span>
                <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden"><div className="h-full rounded" style={{ width: `${(d.count / max) * 100}%`, background: color }} /></div>
                <span className="text-xs font-mono-tech text-slate-700 w-6 text-right">{d.count}</span>
              </div>
            ))}
          </div>}
    </Card>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: `${color}1a` }}><Icon className="h-5 w-5" style={{ color }} /></div>
      <div><div className="text-2xl font-head font-bold font-mono-tech text-slate-900">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </Card>
  );
}

export default function InspectionReports() {
  const [data, setData] = useState(null);
  const [dataset, setDataset] = useState("inspections");
  const [busy, setBusy] = useState("");
  const [projects, setProjects] = useState([]);
  const [protoProj, setProtoProj] = useState("");
  const [protoBusy, setProtoBusy] = useState("");
  const [archProj, setArchProj] = useState("");
  const [archStart, setArchStart] = useState("");
  const [archEnd, setArchEnd] = useState("");
  const [archBusy, setArchBusy] = useState(false);

  const load = useCallback(() => api.get("/inspections/reports").then((r) => setData(r.data)).catch(() => setData(null)), []);
  const loadProjects = useCallback(() => api.get("/inspections/projects").then((r) => setProjects(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadProjects(); }, [load, loadProjects]);

  const doExport = async (format) => {
    setBusy(format);
    try {
      const r = await api.get(`/inspections/export/${dataset}?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `pruefdoc_${dataset}.${format === "xlsx" ? "xlsx" : format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Export (${format.toUpperCase()}) erstellt`);
    } catch (e) { toast.error("Export fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const doProtocol = async (format) => {
    if (!protoProj) return toast.error("Bitte zuerst ein Projekt wählen");
    setProtoBusy(format);
    try {
      const r = await api.get(`/inspections/protocol/${protoProj}?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `protokoll.${format === "xlsx" ? "xlsx" : format}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success(`Protokoll (${format.toUpperCase()}) erstellt`);
    } catch (e) { toast.error("Protokoll fehlgeschlagen"); }
    finally { setProtoBusy(""); }
  };

  const doArchive = async () => {
    if (!archProj) return toast.error("Bitte zuerst ein Projekt wählen");
    setArchBusy(true);
    try {
      const p = new URLSearchParams();
      if (archStart) p.set("start", archStart);
      if (archEnd) p.set("end", archEnd);
      const r = await api.get(`/inspections/project-archive/${archProj}?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Pruefarchiv.zip"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success("ZIP-Archiv erstellt");
    } catch (e) { toast.error("Archiv fehlgeschlagen"); }
    finally { setArchBusy(false); }
  };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={ClipboardCheck} label="Aktive Prüfungen" value={data.total_active} color="#2563EB" />
        <Stat icon={AlarmClock} label="Überfällig" value={data.total_overdue} color="#DC2626" />
        <Stat icon={AlertTriangle} label="Offene Mängel" value={data.open_measures} color="#EA580C" />
        <Stat icon={Archive} label="Archiv-Einträge" value={data.total_records} color="#16A34A" />
      </div>

      <Card className="p-4">
        <h4 className="font-head font-bold text-slate-900 text-sm mb-3 flex items-center gap-2"><FileDown className="h-4 w-4 text-primary" />Datenexport</h4>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={dataset} onValueChange={setDataset}>
            <SelectTrigger className="w-48" data-testid="export-dataset"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="inspections">Prüfungen & Wartungen</SelectItem>
              <SelectItem value="measures">Mängel & Maßnahmen</SelectItem>
              <SelectItem value="records">Prüfungsarchiv</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => doExport("csv")} disabled={!!busy} data-testid="export-csv">{busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}CSV</Button>
          <Button variant="outline" onClick={() => doExport("xlsx")} disabled={!!busy} data-testid="export-xlsx">{busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Excel</Button>
          <Button variant="outline" onClick={() => doExport("pdf")} disabled={!!busy} data-testid="export-pdf">{busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}PDF</Button>
        </div>
      </Card>

      <Card className="p-4" data-testid="protocol-card">
        <h4 className="font-head font-bold text-slate-900 text-sm mb-1 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Projekt-Protokoll (mit Status)</h4>
        <p className="text-xs text-slate-500 mb-3">Druckbares Protokoll aller Prüfungen & Wartungen eines Projekts inkl. aktuellem Status.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={protoProj} onValueChange={setProtoProj}>
            <SelectTrigger className="w-64" data-testid="protocol-project"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Ohne Projektzuordnung</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{typeof p.inspection_count === "number" ? ` (${p.inspection_count})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => doProtocol("pdf")} disabled={!!protoBusy || !protoProj} data-testid="protocol-pdf">{protoBusy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}Protokoll drucken (PDF)</Button>
          <Button variant="outline" onClick={() => doProtocol("xlsx")} disabled={!!protoBusy || !protoProj} data-testid="protocol-xlsx">{protoBusy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}Excel</Button>
          <Button variant="outline" onClick={() => doProtocol("csv")} disabled={!!protoBusy || !protoProj} data-testid="protocol-csv">{protoBusy === "csv" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}CSV</Button>
        </div>
      </Card>

      <Card className="p-4" data-testid="archive-card">
        <h4 className="font-head font-bold text-slate-900 text-sm mb-1 flex items-center gap-2"><FileArchive className="h-4 w-4 text-primary" />Projekt-Archiv als ZIP (für Überprüfung)</h4>
        <p className="text-xs text-slate-500 mb-3">Alle Protokolle, Dokumente und Fotos der wiederkehrenden Wartungen & Prüfungen eines Projekts – gesammelt als ZIP, optional für einen frei wählbaren Zeitraum.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Projekt</label>
            <Select value={archProj} onValueChange={setArchProj}>
              <SelectTrigger className="w-60" data-testid="archive-project"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">Ohne Projektzuordnung</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{typeof p.inspection_count === "number" ? ` (${p.inspection_count})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Von</label>
            <Input type="date" value={archStart} onChange={(e) => setArchStart(e.target.value)} className="w-40 h-9" data-testid="archive-start" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 block mb-1">Bis</label>
            <Input type="date" value={archEnd} onChange={(e) => setArchEnd(e.target.value)} className="w-40 h-9" data-testid="archive-end" />
          </div>
          <Button onClick={doArchive} disabled={archBusy || !archProj} data-testid="archive-download">
            {archBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileArchive className="h-4 w-4 mr-2" />}ZIP herunterladen
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Ohne Zeitraum werden alle Dokumente einbezogen. Das Protokoll (Deckblatt) listet immer alle Vorgänge des Projekts mit Status.</p>
      </Card>
      <div className="grid md:grid-cols-2 gap-3">
        <BarList title="Prüfungen nach Fachbereich" data={data.by_category} />
        <BarList title="Prüfungen nach Gebäude" data={data.by_building} color="#059669" />
        <BarList title="Prüfungen nach Verantwortlichem" data={data.by_responsible} color="#7c3aed" />
        <BarList title="Überfällig nach Fachbereich" data={data.overdue_by_category} color="#DC2626" />
        <BarList title="Mängel nach Status" data={data.measures_by_status} color="#EA580C" />
        <BarList title="Abgeschlossene Prüfungen pro Jahr" data={data.completed_by_year} color="#0D9488" />
      </div>
    </div>
  );
}
